import React, { useRef } from 'react';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import useLanguagesHooks from '../../Languages/setLanguage-hooks';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import AuthError from '../../AuthError/authError';
import { IAutocompleteData, IAutocompleteItem } from '../../../Interfaces/componentTypes';
import styles from '../AuthInput/authInputs.module.scss';

interface IAutoCompleteProps {
    data: IAutocompleteData | undefined;
    label?: string;
    disable?: boolean;
    id?: string;
    value?: IAutocompleteItem | null;
    name?: string;
    onChange: (arg: IAutocompleteItem | null) => void;
    placeholder?: string;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
    showTextError?: boolean;
    style?: string;
    labelStyle?: any;
}

const AutoComplete: React.FC<IAutoCompleteProps> = (props) => {
    const { data, label, error, onChange, id, placeholder, disable, value, showTextError = true, style, labelStyle } =
        props;
    const { t } = useLanguagesHooks();
    const options = (data ?? []).map((item) => item);
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <div className={`${styles.container} ${style}`}>
            {label
                &&
                <label htmlFor={id} className={`${styles.label} ${labelStyle}`}>
                    {label!}
                    {error && <span style={{ color: '#b00020', marginLeft: '5px' }}>*</span>}
                </label>
            }
            <div >
                <Autocomplete
                    value={!value ? null : value}
                    disabled={disable}
                    disablePortal
                    id={id}
                    fullWidth={true}
                    options={options}
                    getOptionLabel={(options) => options.title}
                    noOptionsText={<span className={styles.noResult}>{t('No_Data.No_Search')}</span>}
                    onChange={(_, selected) => {
                        onChange(selected);
                        if (inputRef.current) {
                            inputRef.current.blur();
                        }
                    }}
                    multiple={false}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            placeholder={placeholder}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                ...params.inputProps,
                                readOnly: true,
                                inputMode: 'none',
                            }}
                            InputProps={{
                                ...params.InputProps,
                            }}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: 'gray',
                                    fontStyle: 'italic',
                                    transition: 'all 0.2s ease',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    display: 'none',
                                },
                                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                    display: 'none',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'var(--text-dark)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--text-dark)',
                                        borderWidth: '1px',
                                    },
                                    ...(error && {
                                        '& fieldset': {
                                            borderColor: '#b00020',
                                        },
                                    }),
                                    '& .MuiAutocomplete-popupIndicator': {
                                        color: 'var(--text-dark)',
                                    },
                                },

                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                            {option.icon && (
                                <span style={{ marginRight: '10px' }}>
                                    <img src={option.icon} alt={option.title} />
                                </span>
                            )}
                            {option.title}
                        </li>
                    )}
                />

            </div>
            {showTextError && <AuthError text={error && error.message} />}
        </div>
    );
};

export default AutoComplete;
